import { APP_CONFIG } from '../../core/config';
import { buildDispatchCard } from '../../core/dispatch-card';
import { DISPATCH_GROUPING_LABELS, crewIdentity, personalIdLabel } from '../../core/dispatch-report';
import type {
  DispatchCrewLine,
  DispatchReport,
  DispatchRow,
  DispatchTeamLine,
} from '../../core/dispatch-report';
import { DispatchOrderCard } from '../tasks/DispatchOrderCard';
import { ShareDispatchButton } from '../tasks/ShareDispatchButton';
import { DISPATCH_VIEWS, DEFAULT_DISPATCH_VIEW } from './dispatch-views';
import type { DispatchView } from './dispatch-views';
import {
  CREW_ROLE_LABELS,
  ESCORT_OPTION_LABELS,
  EXTERNAL_PERSON_TAG,
} from '../../types/assignment';

interface DailyDispatchReportProps {
  report: DispatchReport;
  /** Who printed the sheet — it is signed, like a written פקודת יום. */
  printedBy: string;
  printedAt: number;
  /**
   * Portrait cards or the wide table. The screen chooses — a phone opens on
   * {@link DISPATCH_VIEWS} `cards`, so a task is never cut off at the edge.
   */
  view?: DispatchView;
}

const stamp = (value: number): string => {
  const date = new Date(value);
  const pad = (part: number) => String(part).padStart(2, '0');
  return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

/**
 * The two numbers a name on the sheet is signed for by — the personal number
 * and the mobile — set left-to-right so neither reads backwards in an RTL cell.
 * The label itself comes from the engine; an unknown number is omitted rather
 * than printed as a dash.
 */
function CrewNumbers({ person }: { person: DispatchCrewLine }) {
  const personalId = personalIdLabel(person.personalNumber);
  if (!personalId && !person.phone) return null;
  return (
    <span className="text-ink/60">
      {' ('}
      {personalId ? <span data-dir="ltr">{personalId}</span> : null}
      {personalId && person.phone ? ' | ' : ''}
      {person.phone ? <span data-dir="ltr">{person.phone}</span> : null}
      {')'}
    </span>
  );
}

/**
 * The crew of one vehicle, as one cell of the sheet. Only assigned seats are
 * printed: a vehicle riding without a task commander carries no commander row
 * at all, rather than a row with a dash in it. `טרם נקבע מפקד משימה` is not an
 * absence — it is a decision still open, and it stays on the sheet.
 *
 * Every name carries its מ.א. beside it, the same way the portrait card and the
 * shared image name a soldier — a printed sheet is signed against, and a name
 * alone is not enough to sign against.
 */
function CrewCell({ team }: { team: DispatchTeamLine }) {
  return (
    <div className="space-y-0.5">
      <p>
        <span className="text-ink/50">נהג: </span>
        {team.driver ? (
          <>
            <span className="font-medium">{team.driver.name}</span>
            <CrewNumbers person={team.driver} />
          </>
        ) : (
          <span className="text-ink/50">טרם נקבע</span>
        )}
      </p>
      {team.escortLabel === ESCORT_OPTION_LABELS.none ? null : (
        <p>
          <span className="text-ink/50">{CREW_ROLE_LABELS.escort}: </span>
          <span>{team.escortLabel}</span>
          {/* Only a named soldier has numbers — "טרם נקבע מפקד משימה" is a decision. */}
          {team.escort ? <CrewNumbers person={team.escort} /> : null}
        </p>
      )}
      {team.passengers.length > 0 ? (
        <p>
          <span className="text-ink/50">נוסעים: </span>
          {team.passengers
            .map((passenger) =>
              passenger.external
                ? `${crewIdentity(passenger)} (${EXTERNAL_PERSON_TAG})`
                : crewIdentity(passenger),
            )
            .join(' · ')}
        </p>
      ) : null}
      {team.teamNote ? <p className="text-ink/60">הערה: {team.teamNote}</p> : null}
    </div>
  );
}

/** The vehicle cell — the three-digit internal number is the anchor of the row. */
function VehicleCell({ team }: { team: DispatchTeamLine }) {
  return (
    <div>
      <span className="print-vehicle-id text-lg font-bold tabular-nums" data-dir="ltr">
        {team.vehicleMilitaryId}
      </span>
      <p className="text-xs">{team.vehicleName}</p>
      <p className="text-xs text-ink/60" data-dir="ltr">
        {team.plateNumber}
      </p>
      <p className="text-xs text-ink/50">{team.vehicleTypeLabel}</p>
    </div>
  );
}

/** One task: as many rows as it has vehicles, at least one. */
function TaskRows({ row }: { row: DispatchRow }) {
  const cellClass = 'border border-line align-top p-2 text-sm';
  const span = Math.max(1, row.teams.length);

  const taskCells = (
    <>
      <td className={`${cellClass} whitespace-nowrap`} rowSpan={span}>
        <span className="font-semibold tabular-nums" data-dir="ltr">
          {row.window || row.startTime}
        </span>
        <p className="text-xs text-ink/50">{row.dateLabel}</p>
      </td>
      <td className={cellClass} rowSpan={span}>
        <p className="font-semibold">{row.title}</p>
        <p className="text-xs text-ink/60">
          {row.typeName ? `${row.typeName} · ` : ''}
          {row.priorityLabel} · {row.statusLabel}
        </p>
        {row.operationalNotes ? (
          <p className="text-xs text-ink/60 mt-1">{row.operationalNotes}</p>
        ) : null}
      </td>
    </>
  );

  const destinationCells = (
    <>
      <td className={cellClass} rowSpan={span}>
        <p>{row.destination || '—'}</p>
      </td>
      <td className={cellClass} rowSpan={span}>
        {row.contactName ? (
          <>
            <p>
              {row.contactName}
              {row.contactRole ? <span className="text-ink/50"> · {row.contactRole}</span> : null}
            </p>
            <p className="tabular-nums" data-dir="ltr">
              {row.contactPhone}
            </p>
          </>
        ) : (
          <span className="text-ink/50">—</span>
        )}
      </td>
    </>
  );

  if (row.teams.length === 0) {
    return (
      <tr className="break-inside-avoid">
        {taskCells}
        <td className={cellClass} colSpan={2}>
          <span className="text-ink/50">טרם שובצו רכב וצוות</span>
        </td>
        {destinationCells}
      </tr>
    );
  }

  return (
    <>
      {row.teams.map((team, index) => (
        <tr key={team.teamId} className="break-inside-avoid">
          {index === 0 ? taskCells : null}
          <td className={cellClass}>
            <VehicleCell team={team} />
          </td>
          <td className={cellClass}>
            <CrewCell team={team} />
          </td>
          {index === 0 ? destinationCells : null}
        </tr>
      ))}
    </>
  );
}

/**
 * The printable daily dispatch sheet (דף שילוח / פקודת יום).
 *
 * Purely presentational: every row, group and total is built by
 * `src/core/dispatch-report.ts` and handed in ready. The layout is optimised
 * for paper — the `print-sheet` class in `src/index.css` strips the panel
 * chrome, keeps a group on one page and repeats the table head across pages.
 */
export function DailyDispatchReport({
  report,
  printedBy,
  printedAt,
  view = DEFAULT_DISPATCH_VIEW,
}: DailyDispatchReportProps) {
  const headClass = 'border border-line bg-canvas p-2 text-xs font-semibold text-ink/70 text-right';

  return (
    <article className="print-sheet bg-panel border border-line rounded-2xl p-5">
      <header className="border-b border-line pb-3 mb-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-xl font-bold">דף שילוח יומי — פקודת יום</h2>
            <p className="text-sm text-ink/60">
              {APP_CONFIG.appName} · {report.rangeLabel} · {DISPATCH_GROUPING_LABELS[report.groupBy]}
            </p>
          </div>
          <dl className="text-xs text-ink/60 text-left shrink-0">
            <div>
              <dt className="inline">הופק: </dt>
              <dd className="inline tabular-nums" data-dir="ltr">
                {stamp(printedAt)}
              </dd>
            </div>
            <div>
              <dt className="inline">על ידי: </dt>
              <dd className="inline">{printedBy}</dd>
            </div>
          </dl>
        </div>
        <p className="text-sm mt-2">
          סה״כ <strong className="tabular-nums">{report.totals.tasks}</strong> משימות ·{' '}
          <strong className="tabular-nums">{report.totals.vehicles}</strong> רכבים ·{' '}
          <strong className="tabular-nums">{report.totals.crew}</strong> אנשי צוות
        </p>
      </header>

      {report.groups.length === 0 ? (
        <p className="text-sm text-ink/60">אין משימות בטווח התאריכים שנבחר.</p>
      ) : (
        report.groups.map((group) => (
          <section key={group.key} className="print-group mb-5">
            <h3 className="text-sm font-bold bg-canvas border border-line rounded-lg px-3 py-1.5 mb-2">
              {group.title} · {group.rows.length} משימות
            </h3>

            {/* A phone reads the group as a column of portrait orders — the same
                rows, without a single horizontal scroll. */}
            {view === 'cards' ? (
              <div className="space-y-3">
                {group.rows.map((row) => {
                  const card = buildDispatchCard(row);
                  return (
                    <DispatchOrderCard
                      key={row.taskId}
                      card={card}
                      actions={<ShareDispatchButton card={card} />}
                    />
                  );
                })}
              </div>
            ) : (
            <table className="w-full border-collapse">
              <thead>
                <tr>
                  <th className={headClass}>שעה</th>
                  <th className={headClass}>משימה וסוג</th>
                  <th className={headClass}>רכב · מספר פנימי</th>
                  <th className={headClass}>צוות</th>
                  <th className={headClass}>יעד</th>
                  <th className={headClass}>איש קשר</th>
                </tr>
              </thead>
              <tbody>
                {group.rows.map((row) => (
                  <TaskRows key={row.taskId} row={row} />
                ))}
              </tbody>
            </table>
            )}
          </section>
        ))
      )}

      <footer className="border-t border-line pt-3 text-xs text-ink/50">
        הופק ממערכת {APP_CONFIG.appName} · המסמך מיועד לשימוש פנימי ביחידה בלבד.
      </footer>
    </article>
  );
}
